/* eslint-disable @typescript-eslint/no-explicit-any */
import {ROWS_PER_PAGE_LIMIT, USER_ROLES} from './constants';
import {categoriesTypesMap} from './maps';
import {ROUTES} from './routes';

export const transformB2C = (
  t: (key: string) => string,
  categories: {key: string; id: number}[] | undefined = []
) => {
  // Ensure categories is an array before calling map
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories
    ?.map(({key, id}) => ({...categoriesTypesMap?.[key], id, key}))
    .filter(el => !!el?.titleId)
    .map(({id, titleId, icon, key}) => ({
      name: t(titleId),
      key,
      icon,
      id
    }));
};

export const getPageCount = (
  total: number,
  limit: number = ROWS_PER_PAGE_LIMIT
) => Math.ceil((total || 1) / limit);

export const getAuthRedirectRoute = (role: USER_ROLES) => {
  switch (role) {
    case USER_ROLES.member:
      return ROUTES.feed;
    case USER_ROLES.merchant:
      return ROUTES.overview;
    case USER_ROLES.organisation:
      return ROUTES.merchants;
    default:
      return ROUTES.home;
  }
};

export const extractInappropriateSymbols = (str: string): string =>
  str
    ? [...new Set(str.match(/[^A-Za-z0-9!@#$*_]/g) || [])]
        .map(ch => `"${ch}"`)
        .join(', ')
    : '';

export const convertToUTCISOString = (dateStr?: string): string | null => {
  if (!dateStr) return null;
  const originalDate = new Date(dateStr);
  return originalDate.toISOString();
};

/**
 * Formats an error message - handles both strings and arrays
 */
export const formatErrorMessage = (message: any): string | null => {
  if (!message) return null;

  if (Array.isArray(message)) {
    // Join array of validation errors with newlines for better readability
    return message.join('\n');
  }

  if (typeof message === 'string') {
    return message;
  }

  return null;
};

/**
 * Extracts error message from API error response
 * Checks multiple possible locations where error messages might be nested
 * Handles both single string messages and arrays of validation errors
 */
export const extractErrorMessage = (error: any): string => {
  if (!error) return 'An error occurred';

  // Check various possible error message locations
  // React Query / API client error structures
  // Priority order matters - check most specific paths first
  const message =
    formatErrorMessage(error?.data?.error?.message) ||
    formatErrorMessage(error?.error?.response?.message) ||
    formatErrorMessage(error?.error?.message) ||
    formatErrorMessage(error?.data?.message) ||
    formatErrorMessage(error?.message) ||
    formatErrorMessage(error?.response?.data?.error?.message) ||
    formatErrorMessage(error?.response?.message) ||
    formatErrorMessage(error?.response?.error?.message) ||
    formatErrorMessage(error?.response?.data?.message);

  return message || 'An error occurred';
};

export const formatAddress = (
  {country, city, street, apartment, zip} = {} as {
    street?: string;
    country?: string;
    city?: string;
    zip?: string;
    apartment?: string;
  }
) => {
  return [street, apartment, zip, city, country].filter(Boolean).join(', ');
};

export const isOverflown = element => {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
};
export const normalizeCategoriesForChart = (
  arr: {
    key: string;
    value: number;
  }[],
  t
) => {
  const sorted = [...arr].sort((a, b) => b.value - a.value);

  const mainCategories = sorted.slice(0, 4);

  const sumValueOthers = sorted
    .slice(4)
    .reduce((acc, item) => acc + item.value, 0);

  return [
    ...mainCategories,
    ...(sorted.length > 4
      ? [{key: t('FinancesPage.other'), value: sumValueOthers}]
      : [])
  ];
};

export const formatThousands = val => {
  if (val >= 1000) return (val / 1000).toFixed(val % 1000 === 0 ? 0 : 1) + 'k';
  return val.toString();
};

export const assignNonRepeatingColors = (
  data: any[],
  colors: string[],
  colorKey: string = 'fill'
) => {
  const result: any[] = [];

  data?.forEach((item, index) => {
    let newColor = colors[Math.floor(Math.random() * colors.length)];

    if (index > 0) {
      while (newColor === result[index - 1][colorKey]) {
        newColor = colors[Math.floor(Math.random() * colors.length)];
      }
    }

    result.push({
      ...item,
      [colorKey]: newColor
    });
  });

  return result;
};
